
const Util = require('util');

const PictApplication = require('pict-application');
const PictSectionForm = require('pict-section-form');
const PictFormMetacontroller = require('pict-section-form/source/views/Pict-View-Form-Metacontroller.js');
const PictSectionModal = require('pict-section-modal');
const HeadlightQRCodeBundle = require('headlight-qrcode-web');

const HeadlightRestClient = require('headlight-rest-client');
const HeadlightViewSignatures = require('headlight-view-signatures');
const HeadlightPanels = require('headlight-view-panels');
const HeadlightViewLogin = require('headlight-view-login');

const InputTypeBehaviorProvider = require('./providers/InputTypeBehaviorProvider.js');
const DistillingProvider = require('./providers/DistillingProvider.js');
const AutoSaveProvider = require('./providers/AutoSaveProvider.js');
const DocumentFrameProvider = require('./providers/DocumentFrameProvider.js');

const ManifestConditionEvaluator = require('./services/ManifestConditionEvaluator.js');
const HeadlightFormLoadTiming = require('./services/Headlight-FormLoadTiming.js');

const libHeadlightQuickSummariesService = require('../../../../lib/web/headlight-quick-summaries/Headlight-Quick-Summaries-Service.js');

//FIXME: hack to hook into onAfterRenderAsync to perform local initialization
const originalPictFormMetacontrollerOnAfterRenderAsync = PictFormMetacontroller.prototype.onAfterRenderAsync;
PictFormMetacontroller.prototype.onAfterRenderAsync = function(pRenderable)
{
	const res = originalPictFormMetacontrollerOnAfterRenderAsync.call(this, pRenderable);
	this.onAfterRenderAsyncCallbacks?.forEach((callback) => callback());
	return res;
};

//FIXME: hack to hook into onAfterRender to perform local initialization
const originalPictFormMetacontrollerOnAfterMarshalToView = PictFormMetacontroller.prototype.onAfterMarshalToView;
PictFormMetacontroller.prototype.onAfterMarshalToView = function()
{
	const res = originalPictFormMetacontrollerOnAfterMarshalToView.call(this);
	this.onAfterMarshalToViewCallbacks?.forEach((callback) => callback());
	return res;
};

/** @typedef {import('./providers/DistillingProvider.js').FieldDistillingMapping} FieldDistillingMapping */

/**
 * FIXME: figure out how to flow these types through in a more sane way...
 * @typedef {import('pict') & import('fable') &
 * {
 *   HeadlightRestClient: import('headlight-rest-client'),
 *   MetaTemplate: any, // import('fable').MetaTemplate,
 *   views: { PictFormMetacontroller: import('pict-section-form').PictFormMetacontroller },
 *   fable: import('fable'),
 *   PictApplication: HeadlightConfigurableApplication,
 *   providers: {
 *     Input: import('./providers/InputTypeBehaviorProvider'),
 *     Distilling: import('./providers/DistillingProvider'),
 *     AutoSave: import('./providers/AutoSaveProvider'),
 *     PictFormSectionDefaultTemplateProvider: import('pict-section-form').PictFormTemplateProvider,
 *   },
 *   options: {
 *     Timezone: string,
 *     [key: string]: any,
 *   },
 * }} Pict
 */

/** @typedef {import('fable')} Fable */
/** @typedef {(pError?: Error) => void} ErrorCallback */


/**
 * Base class for Pict applications with configuration driven UI.
 *
 * Comes with pre-registered stock views and providers.
 *
 * NOTE: This class is a merge of each layer with an attempt to normalize and refactor.
 */
class HeadlightConfigurableApplication extends PictApplication
{
	/**
	 * Apply default options to the passed in options, if absent.
	 *
	 * @param {Object} pOptions - The options to apply defaults to.
	 */
	static applyDefaultOptions(pOptions)
	{
		if (!pOptions.Login)
		{
			pOptions.Login = { };
		}
		if (!('Required' in pOptions.Login))
		{
			pOptions.Login.Required = true;
		}
		if (!pOptions.Document)
		{
			pOptions.Document = { };
		}
		if (!('Required' in pOptions.Document))
		{
			pOptions.Document.Required = true;
		}
	}

	/**
	 * Check whether a builtin section should be rendered.
	 *
	 * Builtin section values follow this convention:
	 *   - `undefined` / `null` → enabled (shown by default)
	 *   - `false`              → disabled (hidden)
	 *   - `true`               → enabled
	 *   - any other value (e.g. a template string) → enabled (future use)
	 *
	 * @param {boolean | string | undefined | null} pBuiltinValue - The value from `manifest.Builtins.<Section>`.
	 * @return {boolean} `true` if the section should be shown, `false` if it should be hidden.
	 */
	static isBuiltinSectionEnabled(pBuiltinValue)
	{
		if (pBuiltinValue === undefined || pBuiltinValue === null)
		{
			return true;
		}
		return pBuiltinValue !== false;
	}

	/**
	 * Perform any pre-processing needed on the form definition.
	 *
	 * @param {Object<String, any>} formDefinition - The form definition to preprocess.
	 *
	 * @return {Object<String, any>} - The processed form definition. (it's just the original one, mutated)
	 */
	static preprocessFormDefinition(formDefinition)
	{
		//NOTE: This prevents PictSectionForm from automatically bootstrapping views and allows us to control when that happens.
		if (formDefinition?.DefaultFormManifest)
		{
			formDefinition.MyDefaultFormManifest = formDefinition.DefaultFormManifest;
			delete formDefinition.DefaultFormManifest;
		}
		// duck typing
		if (!formDefinition?.MyDefaultFormManifest?.Descriptors)
		{
			console.error(`Form manifest Descriptors is missing. No form configuration found.`, { formDefinition });
			return formDefinition;
		}
		let descriptors = formDefinition.MyDefaultFormManifest.Descriptors;
		descriptors = HeadlightConfigurableApplication.repairDescriptorMap(descriptors) || descriptors;
		descriptors = HeadlightConfigurableApplication.backfillWidthValueForDescriptors(descriptors) || descriptors;
		for (const descriptor of Object.values(descriptors))
		{
			InputTypeBehaviorProvider.validateDescriptor(descriptor);
			if (descriptor.PictForm?.DistillingKey)
			{
				if (descriptor.PictForm?.SelectOptionsPickList)
				{
					continue;
				}
				const tmpPickLists = formDefinition.MyDefaultFormManifest.PickLists || [];
				formDefinition.MyDefaultFormManifest.PickLists = tmpPickLists;
				const tmpPickListHash = `Distilling-${descriptor.PictForm.DistillingBundleHash}-${descriptor.PictForm.DistillingKey}`;
				descriptor.PictForm.SelectOptionsPickList = tmpPickListHash;
				if (!tmpPickLists.find((pl) => pl.Hash === tmpPickListHash))
				{
					tmpPickLists.push({ Hash: tmpPickListHash, Dynamic: true });
				}
			}
		}
		formDefinition.MyDefaultFormManifest.Descriptors = descriptors;
		return formDefinition;
	}

	/**
	 * Align all sections to 12 columns
	 *
	 * TODO: Pretty sure we can do this inside pict by making a default value but not sure how off the top of my head. Pre-process descriptors here
	 *
	 * @param {any} descriptorMap - The descriptor map to backfill.
	 *
	 * @return {any} - The backfilled descriptor map.
	 */
	static backfillWidthValueForDescriptors(descriptorMap)
	{
		if (Array.isArray(descriptorMap))
		{
			console.error(`Form manifest Descriptors is an array. Fatal error. Cannot continue.`, { descriptorMap });
			throw new Error('Descriptors should be an object, not an array');
		}

		for (const descriptor of Object.values(descriptorMap))
		{
			descriptor.Width = descriptor.Width || 1;
		}

		return descriptorMap;
	}

	/**
	 * Repair the descriptor map to be an object instead of an array.
	 *
	 * @param {any} descriptorMap - The descriptor map to repair.
	 *
	 * @return {Object<String, any> | any} - The repaired descriptor map, or the original value if it is not an array.
	 */
	static repairDescriptorMap(descriptorMap)
	{
		if (Array.isArray(descriptorMap))
		{
			console.error(`Form manifest Descriptors is an array which is not supported. Converting to object.`, { descriptorMap });
			//FIXME: this is a hack to make sure this works - template needs to be converted
			const newDescriptorMap = { };
			for (const descriptor of descriptorMap)
			{
				if (descriptor.Hash in newDescriptorMap)
				{
					console.error(`Duplicate descriptor hash found. Skipping.`, { descriptor });
					continue;
				}
				newDescriptorMap[descriptor.Hash] = descriptor;
			}
			console.trace(`Converted Descriptors to object.`, { newDescriptorMap });
			if (Object.keys(newDescriptorMap).length != descriptorMap.length)
			{
				console.error(`Descriptor map array had duplicate keys. Converted object is smaller. (${Object.keys(newDescriptorMap).length} != ${descriptorMap.length})`);
			}
			return newDescriptorMap;
		}
		return descriptorMap;
	}

	/**
	 * @param {Fable} pFable - The fable instance.
	 * @param {Object} [pOptions] - The options for the application.
	 * @param {String} [pServiceHash] - The fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = (pOptions || {});
		HeadlightConfigurableApplication.applyDefaultOptions(tmpOptions);
		super(pFable, tmpOptions, pServiceHash);

		// Phase 0 form-load instrumentation. Constructed first so `Boot` is marked as early as the
		// application can mark anything; its startTime carries everything the page did before this.
		// See services/Headlight-FormLoadTiming.js for the phases and how to read the numbers.
		/** @type {HeadlightFormLoadTiming} */
		this.FormLoadTiming = new HeadlightFormLoadTiming(
		{
			Log: this.pict?.log,
			Context: this.options?.ApplicationName || 'Form',
		});
		this.markFormLoadPhase('Boot');

		/**
		 * FIXME: figure out how to flow these types through in a more sane way...
		 * @type {Pict}
		 */
		this.pict;

		/** @type {Fable} */
		this.fable;

		/** @type {any} */
		this.options;

		this.registerServiceTypes(
		{
			HeadlightRestClient,
			HeadlightStationing: require('../../../../lib/service/headlight-stationing-service/Headlight-Stationing.js'),
			HeadlightQRCodeHash: require('headlight-qrcode-hash'),
			HeadlightHintHighlighting: require('./services/Headlight-HintHighlighting.js'),
			HeadlightDocumentService: require('./services/Headlight-Document-Service.js'),
		});

		this.instantiateServices(
		{
			HeadlightRestClient: { ServerURL: this.options.HeadlightAPIURL || '/1.0/' },
			HeadlightStationing: { },
			HeadlightQRCodeHash: { },
			HeadlightHintHighlighting: { },
			HeadlightDocumentService: { },
		});

		/** @type {HeadlightViewSignatures} */
		this.pict.views.HeadlightSignatures;
		/**
		 * @type {HeadlightViewLogin}
		 */
		this.pict.views.HeadlightLogin;

		/**
		 * @type {Object<String, any>}
		 */
		this.services;

		// Add the pict form service
		this.pict.addServiceType('PictSectionForm', PictSectionForm);

		// Add the pict form metacontroller service, which provides programmaatic view construction from manifests and render/marshal methods.
		this.pict.addView('PictFormMetacontroller', {}, PictSectionForm.PictFormMetacontroller);

		this.pict.views.PictFormMetacontroller.onAfterRenderAsyncCallbacks = [];
		this.pict.views.PictFormMetacontroller.onAfterMarshalToViewCallbacks = [];

		// pict-section-modal — the canonical modal/tooltip view. Registered here in
		// the shared base so every web app and document that renders dynamic-input
		// forms has the tooltip surface the chips use (PictProvider-ChipTooltips
		// attaches pinnable tooltips to chip anchors). Guarded so an app that
		// already registered it (e.g. the data dashboards) isn't clobbered. Chips
		// degrade to native title tooltips if this is ever absent.
		if (!this.pict.views['Pict-Section-Modal'])
		{
			this.pict.addViewSingleton('Pict-Section-Modal', PictSectionModal.default_configuration, PictSectionModal);
		}
		/**
		 * @type {{
		 *	Interactive: boolean,
		 *	InitialSaveRequired: boolean,
		 *	Status: string,
		 *	DistillingMapping: Array<FieldDistillingMapping>,
		 *	Select2OptionsCache: Object<String, Array<{ id: string, text: string }>>,
		 *	DocumentData: any,
		 *	CurrentUser: any,
		 *	[key: string]: any, // allow for arbitrary data to be added
		 * }}
		 */
		this.AppData;
		// this is set on each index.html for the respective document behavior
		this.AppData.Interactive = window._InteractiveForm;

		// log noise level for pict
		this.pict.LogNoisiness = this.options.LogNoisiness ?? 0;

		//FIXME: what layer does this belong at - some kind of "reports" customization layer?
		const tempSessionToken = this.pict.ContentAssignment.readContent('#DocumentObjectSessionToken');
		if (tempSessionToken)
		{
			this.pict.HeadlightRestClient.setUrlParam('SessionToken', String(tempSessionToken));
		}

		// do a basic "to lower case" operation on the data
		this.pict.MetaTemplate.addPattern('{~ToLowerCase:', '~}', (pHash, pData, pContextArray) =>
		{
			const value = this.pict.manifest.getValueAtAddress(pData, pHash);
			return (value === undefined || value === null) ? '' : String(value).toLowerCase();
		});

		// create disabled attribute for values that are set to true
		this.pict.MetaTemplate.addPattern('{~Attr:', '~}', (pHash, pData, pContextArray) =>
		{
			// split on first colon only
			const [ attr, path ] = pHash.split(/:(.*)/s);
			if (!path)
			{
				this.pict.log.error('Invalid path in {~Attr:...~} pattern', pHash);
				return '';
			}

			let value = this.pict.manifest.getValueAtAddress(pData, path);
			if (!value)
			{
				return '';
			}

			// these attributes values should be the same as their name
			// I dont think this is a hard requirement but its cleaner than having random values in there
			if ([ 'disabled', 'readonly' ].includes(attr))
			{
				value = attr;
			}

			return `${attr}="${value}"`;
		});

		// include all three themes for the document (edit, read, print)
		this.registerProviders(
		{
			Input: { Provider: InputTypeBehaviorProvider, Options: { } },
			Distilling: { Provider: DistillingProvider, Options: { } },
			AutoSave: { Provider: AutoSaveProvider, Options: { } },
			DocumentFrame: { Provider: DocumentFrameProvider, Options: { } },
			SessionProvider: { Provider: require('../../../../web/providers/Session-Provider.js'), Options: { } },
		});

		// QR codes (headlight-qrcode-web): the HeadlightQR provider, its views and the `QRCode` form input. The keyboard
		// scanner is off unless an app turns it on (LIMS, the config app) or a scan session holds it (Scan Samples), so a
		// scanner never acts inside a form. Templates are added in registerTemplates().
		this.qrCodeBundle = new HeadlightQRCodeBundle(this.pict);
		this.qrCodeBundle.attach(
			{
				HeadlightQR:
				{
					EntitySource: new HeadlightQRCodeBundle.RestEntitySource(
						{
							getJSON: (pPath) => new Promise((fResolve) => this.pict.HeadlightRestClient.getJSON(pPath, (pError, pResult) => fResolve(pError ? null : pResult))),
						}),
					Wedge: { Enabled: false },
				},
				TemplatePrefixes: { Edit: [], Read: [], Print: [] },
			});

		// callbacks to run after the form is rendered
		this.pict.views.PictFormMetacontroller.onAfterRenderAsyncCallbacks.push(() =>
		{
			this.onAfterRenderAsync();
		});

		// callbacks to run after the form is marshalled to the view
		this.pict.views.PictFormMetacontroller.onAfterMarshalToViewCallbacks.push(() =>
		{
			// only run this if we are in interactive mode
			if (!this.AppData.Interactive)
			{
				return;
			}

			this.pict.log.info('onAfterMarshalToView running...');
			// find elements with dynamic-data-lookup and set their values based on entity and ID
			$('input.dynamic-data-lookup').each((_, element) =>
			{
				const $element = $(element);
				const entityType = $element.attr('data-entity-type');
				const entityDisplayField = $element.attr('data-entity-field');
				const entityIDField = $element.attr('data-entity-id');
				const entityIDElementID = $element.attr('data-entity-html-ref-id');
				const idElement = entityIDElementID ? $(`#${entityIDElementID}`) : null;

				let record;
				switch (entityType)
				{
				case 'Project':
					record = this.AppData.DocumentData?.ReportData?.Project;
					break;
				}
				const id = record?.[entityIDField];
				if (id && idElement)
				{
					idElement.val(id);
				}
				const value = record?.[entityDisplayField];
				if (value)
				{
					$element.val(value);
				}
			});
			this.pict.log.info('onAfterMarshalToView done.');
		});
		this.pict.serviceManager.addAndInstantiateServiceType('HeadlightQuickSummaries', libHeadlightQuickSummariesService);
	}

	/**
	 * Record a form-load phase for the Phase 0 latency instrumentation.
	 *
	 * The base marks Boot, SessionReady, DocumentLoaded and FirstRender itself. Subclasses mark
	 * SpinnerRemoved from their own removeLoadingSpinner(), which is what emits the summary line.
	 * Safe to call from anywhere: unknown or repeated phases are ignored and nothing here throws.
	 *
	 * @param {String} pPhase - One of the phases in services/Headlight-FormLoadTiming.js.
	 * @return {Boolean} True when this call recorded the phase.
	 */
	markFormLoadPhase(pPhase)
	{
		// Every mark call site sits in front of work the form needs (the render callback, the login
		// callback, the spinner teardown), so nothing below this line is allowed to escape.
		let tmpRecorded = false;
		try
		{
			tmpRecorded = this.FormLoadTiming ? this.FormLoadTiming.mark(pPhase) : false;
		}
		catch (pError)
		{
			tmpRecorded = false;
		}
		if (pPhase === 'SpinnerRemoved')
		{
			try
			{
				this.pict.providers.DocumentFrame?.announceReady(this.FormLoadTiming ? this.FormLoadTiming.summary() : null);
			}
			catch (pError)
			{
				this.pict.log.warn(`Could not tell the hosting page the form is ready: ${pError?.message || pError}`);
			}
		}
		return tmpRecorded;
	}

	/**
	 * Marshals data from any rendered dynamic views to application data.
	 */
	marshalDataFromDynamicViewsToAppData()
	{
		this.pict.views.PictFormMetacontroller.marshalFromView();
	}

	/**
	 * Marshals data from the application data to any rendered dynamic views.
	 */
	marshalDataFromAppDataToDynamicViews()
	{
		this.pict.views.PictFormMetacontroller.marshalToView();
	}

	/**
	 * Called during construction to register service types. Override to mutate or add service types.
	 *
	 * @param {Object<String, any>} pServiceTypes - The service types to register.
	 */
	registerServiceTypes(pServiceTypes)
	{
		for (const [ serviceHash, serviceClass ] of Object.entries(pServiceTypes))
		{
			this.fable.serviceManager.addServiceType(serviceHash, serviceClass);
		}
	}

	/**
	 * Called during construction to instantiate services. Override to mutate or add services and options.
	 *
	 * @param {Object<String, Object>} pServiceOptions - The service options to instantiate.
	 */
	instantiateServices(pServiceOptions)
	{
		for (const [ serviceHash, serviceOptions ] of Object.entries(pServiceOptions))
		{
			this.fable.serviceManager.instantiateServiceProvider(serviceHash, serviceOptions);
		}
	}

	/**
	 * Called during construction to register providers. Override to mutate or add providers.
	 *
	 * @param {Object<String, { Provider: any, Options: Object }>} pProviders - The providers to register.
	 */
	registerProviders(pProviders)
	{
		for (const [ providerHash, providerSetup ] of Object.entries(pProviders))
		{
			this.pict.addProvider(providerHash, providerSetup.Options, providerSetup.Provider);
		}
	}

	/**
	 * Called during onInitialize to instantiate shared views. Override to mutate or add shared views.
	 *
	 * @param {Object<String, { View: any, Options: Object }>} pViews - The views to instantiate.
	 */
	instantiateGlobalViews(pViews)
	{
		for (const [ viewHash, viewSetup ] of Object.entries(pViews))
		{
			viewSetup.preInitialize?.(this);
			this.pict.addView(viewHash, viewSetup.Options, viewSetup.View);
			viewSetup.postInitialize?.(this);
		}
	}

	/**
	 * The document's program logo, when its program has one.
	 * @return {string} - The source path for the document logo image
	 */
	getDocumentLogoSource()
	{
		const programId = this.AppData.DocumentData?.ReportData?.IDProgram;
		const programLogo = this.AppData.DocumentData?.ReportData?.ProgramLogoURL;
		if (programId && programLogo)
		{
			return programLogo;
		}
	};

	/**
	 * TEMPORARY? Provide an on-after-render callback for the metacontroller.
	 * @param {Function} [fCallback] - The callback to call when the method has completed.
	 */
	onAfterRenderAsync(fCallback)
	{
		const logoSrc = this.getDocumentLogoSource();
		if (logoSrc)
		{
			const tmpLogoWrapper = document.getElementById('Document-Logo-Wrapper');
			if (tmpLogoWrapper)
			{
				this.pict.ContentAssignment.assignContent('#Document-Logo-Wrapper', /*html*/`<img class="image" src="${ logoSrc }" style="max-height: 100px" alt="Logo" />`);
			}
		}

		if (fCallback)
		{
			fCallback();
		}
	}

	/**
	 * View initialization lifecycle hook.
	 * @return {boolean}
	 */
	onInitialize()
	{
		this.instantiateGlobalViews(
		{
			HeadlightSignatures: { View: HeadlightViewSignatures, Options: { } },
			HeadlightLogin:
			{
				View: HeadlightViewLogin,
				Options: false, // ???
				preInitialize: (app) =>
				{
					// create a login section for every form
					let loginSection = document.getElementById('HeadlightLogin-Standard-Wrap');
					if (!loginSection)
					{
						loginSection = document.createElement('section');
						loginSection.id = 'HeadlightLogin-Standard-Wrap';
						document.body.appendChild(loginSection);
					}
					// Typing a user name is never a scan.
					loginSection.setAttribute('data-headlight-qr-wedge', 'ignore');
				},
			},
			HeadlightPanels:
			{
				View: HeadlightPanels,
				Options: this.options.HeadlightPanels,
				preInitialize: (app) =>
				{
					// create a panel section for every form
					let panelSection = document.getElementById('HeadlightPanels-Container');
					if (!panelSection)
					{
						panelSection = document.createElement('section');
						panelSection.id = 'HeadlightPanels-Container';
						document.body.appendChild(panelSection);
					}
				},
			},
		});
		this.pict.CSSMap.generateCSS();
		this.pict.CSSMap.injectCSS();

		return super.onInitialize();
	}

	/**
	 * @param {Event} event - The event that triggered the logout.
	 * @return {Promise<void>} - A promise that resolves when the logout process is complete.
	 */
	logout(event)
	{
		if (event instanceof Event)
		{
			event.preventDefault();
			event.stopPropagation();
		}
		return this.pict.HeadlightRestClient.getJSON('Deauthenticate', (pError, pResponse) =>
		{
			if (pError)
			{
				this.pict.log.error(`Error deauthenticating: ${pError.message || pError}`);
			}
			this.pict.providers.SessionProvider?.invalidateSession?.();
			this.loginAsync((pError) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error in post-logout loginAsync: ${pError.message || pError}`);
				}
			});
		});
	}

	/**
	 * Loads data for the application.
	 * @param {ErrorCallback} fCallback - The callback to call when the method has completed.
	 */
	onLoadDataAsync(fCallback)
	{
		let tmpError = null;
		this.loadData()
		.then((data) =>
		{
			this.DATA = data;
			this.pict.options.Timezone = data.DocumentData?.ReportData?.Timezone || this.pict.options.Timezone;
			this.pict.Dates.dayJS.tz.setDefault(this.pict.options.Timezone);
			// Marked here rather than in loadData() so a subclass override of loadData() is still covered.
			this.markFormLoadPhase('DocumentLoaded');
		})
		.catch((error) =>
		{
			this.DATA = { };
			this.pict.log.error(`Error loading data: ${error.message || error}`, { Stack: error.stack });
			tmpError = error;
		})
		.finally(() =>
		{
			fCallback(tmpError);
		});
	}

	/**
	 * Loads data for the application.
	 *
	 * @return {Promise<any>}
	 */
	async loadData()
	{
		if (!this.loggedIn)
		{
			this.pict.log.info('Not logged in so not loading data by default.');
			return { };
		}
		const doc = await this.fable.services.HeadlightDocumentService.fetchCurrentDocumentReportParameters();
		// The user record fetch started in onBeforeLoginAsync; make sure it landed before anything can save.
		if (this.AppData.FullUserRecordPromise)
		{
			await this.AppData.FullUserRecordPromise;
		}
		this.pict.options.Timezone = doc?.ReportData?.Timezone || this.pict.options.Timezone;
		this.pict.Dates.dayJS.tz.setDefault(this.pict.options.Timezone);
		this.dataLoaded = true;
		return { DocumentData: doc };
	}

	/**
	 * Lifecycle method called before the document is saved.
	 * FIXME: lock into new Lifecycle
	 *
	 * @return {Promise<void>}
	 */
	async onBeforeDocumentSave()
	{
	}

	/**
	 * Lifecycle method called after the document is saved.
	 * FIXME: lock into new Lifecycle
	 *
	 * @return {Promise<void>}
	 */
	async onAfterDocumentSave()
	{
	}

	/**
	 * Arm periodic auto-saving for this document, if the manifest and the document allow it.
	 *
	 * Called from the load lifecycle once the document, the manifest and the edit/read theme decision
	 * are all in place. Subclasses can override to suppress auto-save for an app whose save path is
	 * not the standard document save.
	 *
	 * @return {boolean} Whether auto-save is now running.
	 */
	startAutoSave()
	{
		const tmpAutoSaveProvider = this.pict.providers?.AutoSave;
		if (!tmpAutoSaveProvider)
		{
			return false;
		}
		try
		{
			return tmpAutoSaveProvider.initializeFromManifest();
		}
		catch (pError)
		{
			// Auto-save is a convenience; a failure to arm it must never break loading the form.
			this.pict.log.error(`Error starting auto-save: ${pError.message || pError}`, { Stack: pError.stack });
			return false;
		}
	}

	/**
	 * Disarm periodic auto-saving.
	 * @param {String} [pReason] - Logged when supplied.
	 * @return {void}
	 */
	stopAutoSave(pReason)
	{
		this.pict.providers?.AutoSave?.stop(pReason);
	}

	/**
	 * @param {*} fCallback
	 */
	onBeforeLoginAsync(fCallback)
	{
		const tmpSessionProvider = this.pict.providers.SessionProvider;
		// On the boot login the SessionProvider synced moments ago during initialize, so reuse that.
		// Every later login (logout, the login form, a session change) asks the server again, since
		// those paths do not all go through logout() to invalidate the cached session.
		const tmpReuseSession = !this._initialLoginCompleted && tmpSessionProvider.hasSession();
		this._initialLoginCompleted = true;
		const tmpSessionLoad = tmpReuseSession ? tmpSessionProvider.getSession() : tmpSessionProvider.syncLoggedInState();
		tmpSessionLoad.then((session) =>
		{
			if (session && session.LoggedIn)
			{
				this.AppData.CurrentUser = session;
				this.loggedIn = true;
				this.pict.log.info(`User is already logged in.`);
				// Load the full user record (ExternalSyncGUID for sample QRCode generation; CheckSession
				// does not carry it) alongside the document instead of before it. loadData awaits the
				// promise, so the record is still in place before any sample is synthesized. Fail-open:
				// a failed fetch leaves FullUserRecord unset and the QRCode falls back.
				// The customer's QR settings (code format) load in the same window, also fail-open.
				const tmpQRCodeHash = this.pict.fable && this.pict.fable.services && this.pict.fable.services.HeadlightQRCodeHash;
				const tmpQRCodeProfileLoad = !tmpQRCodeHash ? Promise.resolve() : tmpQRCodeHash.loadCustomerConfiguration(this.pict.HeadlightRestClient, session.CustomerID)
					.then((pProfile) =>
					{
						if (pProfile && this.pict.providers.HeadlightQR)
						{
							this.pict.providers.HeadlightQR.useProfile(pProfile);
						}
					})
					.catch((pError) => this.pict.log.warn(`Could not load the customer's QR settings: ${(pError && pError.message) || pError}`));
				this.AppData.FullUserRecordPromise = Promise.all([ tmpQRCodeProfileLoad, new Promise((resolve) =>
				{
					this.pict.HeadlightRestClient.getJSON(`User/${session.UserID}`, (pError, pResult) =>
					{
						if (pResult)
						{
							this.AppData.FullUserRecord = pResult;
						}
						resolve();
					});
				}) ]);
			}
			else
			{
				this.loggedIn = false;
				delete this.AppData.CurrentUser;
				this.pict.log.info(`User is not logged in.`);
			}
		})
		.catch((error) =>
		{
			this.pict.log.warn(`session check failed: ${error?.message || error || 'not logged in'}`, { Stack: error?.stack });
			this.loggedIn = false;
		})
		.finally(() =>
		{
			// Marked on both outcomes: a failed session check still ends the session leg.
			this.markFormLoadPhase('SessionReady');
			return fCallback();
		});
	}

	/**
	 *
	 * @param {*} fCallback
	 * @return {void}
	 */
	onLoginAsync(fCallback)
	{
		if (this.loggedIn)
		{
			this.pict.log.info(`User logged in.`);
			this.loadDataAsync((error) =>
			{
				if (error)
				{
					this.pict.log.error(`Error loading data after login: ${error.message || error}`, { Stack: error.stack });
				}
				const tmpManifest = this.pict.settings.MyDefaultFormManifest;
				// The Fable ExpressionParser dereferences identifiers in the expression against the
				// scope object (e.g. `AppData.DocumentData.ReportData.Locked` walks AppData.* in scope).
				const tmpConditionScope = { AppData: this.pict.AppData };
				// Locked loads skip the solve pass by default (SkipSolveWhenLocked defaults true;
				// SkipSolveWhen adds expression-based gating) — re-running section solvers would
				// overwrite the frozen snapshot values with re-derived ones from current state.
				// A manifest opts back in with an explicit SkipSolveWhenLocked: false.
				if (ManifestConditionEvaluator.shouldSkipSolveOnLoad(tmpManifest, tmpConditionScope, this.fable))
				{
					this.pict.log.info('Skipping post-load solve() — manifest condition matched (Locked snapshot).');
				}
				else
				{
					this.solve();
					// A PDF page (each pdf/index.html sets _InteractiveForm = false) renders before this solve, and solve() only writes AppData.
					if (this.pict.AppData.Interactive === false)
					{
						this.marshalDataFromAppDataToDynamicViews();
					}
				}
				this.prepareForPrintingAsync()
				.catch((error) =>
				{
					this.pict.log.error(`Error preparing for printing: ${error.message || error}`, { Stack: error.stack });
				})
				.finally(() =>
				{
					return fCallback();
				});
			});
			return;
		}

		this.pict.log.info(`User logged out.`);
		this.dataLoaded = false;
		this.DATA = { };
		this.loggedIn = false;
		if (this.pict.views.HeadlightLogin && this.options.Login.Required)
		{
			this.pict.views.HeadlightLogin.show();
		}
		fCallback();
	}

	/**
	 * Called after the Pict form views have been bootstrapped from the manifest.
	 * @param {*} fCallback
	 * @return {*}
	 */
	onAfterBootstrapPictFormViewsFromManifestAsync(fCallback)
	{
		return fCallback();
	}

	/**
	 * Called after data has been loaded.
	 * @param {*} fCallback
	 */
	onAfterLoadDataAsync(fCallback)
	{
		// Auto-save is armed here rather than in onInitialize because eligibility depends on state that
		// only exists once the document has loaded: Interactive, Locked, CanEdit, and the manifest.
		const fCallbackWithAutoSave = (pError) =>
		{
			this.startAutoSave();
			return fCallback(pError);
		};
		//TODO: this is a shim that ties the old fake lifecylce into the new lifecycle events - should eventually remove
		this.onDataLoad(this.DATA).catch((error) =>
		{
			this.pict.log.error(`Error in onDataLoad: ${error.message || error}`, { Stack: error.stack });
		}).finally(() =>
		{
			// The InitialBundle only needs the loaded document, so fetch it while the panels render.
			let tmpInitialBundleLoad = null;
			if (this.pict.settings.MyDefaultFormManifest)
			{
				const tmpManifest = this.pict.settings.MyDefaultFormManifest;
				const tmpConditionScope = { AppData: this.pict.AppData };
				const tmpInitialBundle = ManifestConditionEvaluator.shouldSkipInitialBundle(tmpManifest, tmpConditionScope, this.fable) ?
					[] :
					(tmpManifest.InitialBundle || []);
				tmpInitialBundleLoad = new Promise((resolve) =>
				{
					this.pict.EntityProvider.gatherDataFromServer(tmpInitialBundle, (pError) => resolve(pError));
				});
			}
			this.pict.views.HeadlightPanels.renderAsync(() =>
			{
				this.pict.log.info('HeadlightPanels rendered via Async');
				if (!this.pict.settings.MyDefaultFormManifest)
				{
					this.render();
					this.markFormLoadPhase('FirstRender');
					return fCallbackWithAutoSave();
				}
				else
				{
					const tmpBootstrapAndRender = (pError) =>
					{
						// Same decision as the post-load solve() gate, so an explicit
						// SkipSolveWhenLocked: false re-enables solving everywhere.
						this.pict.views.PictFormMetacontroller.AutoSolveOnFirstRender = this.pict.AppData.Interactive
							&& !ManifestConditionEvaluator.shouldSkipSolveOnLoad(this.pict.settings.MyDefaultFormManifest, { AppData: this.pict.AppData }, this.fable);
						this.pict.views.PictFormMetacontroller.FirstRenderCompleted = false;
						this.pict.views.PictFormMetacontroller.bootstrapPictFormViewsFromManifest(this.pict.settings.MyDefaultFormManifest);
						//TODO: this gives us a chance to take actions after everything is prepared but before rendering - think more about this pattern
						this.onAfterBootstrapPictFormViewsFromManifestAsync(() =>
						{
							this.render();
							this.pict.views.PictFormMetacontroller.renderDefaultFormSections();
							// Marked here, not from the metacontroller's onAfterRenderAsyncCallbacks: that list only
							// runs when something calls the metacontroller's renderAsync, and this load path renders
							// synchronously, so the callback never fires on a normal form open.
							// renderDefaultFormSections() is synchronous, so the sections are drawn by this line.
							this.markFormLoadPhase('FirstRender');
							return fCallbackWithAutoSave(pError);
						});
					};

					tmpInitialBundleLoad.then((pError) =>
					{
						tmpBootstrapAndRender(pError);
					}).catch((pError) =>
					{
						this.pict.log.error(`Error bootstrapping the form after the InitialBundle load: ${pError.message || pError}`, { Stack: pError.stack });
					});
				}
			});
		});
	}

	/**
	 * Draw the signature section from the loaded document's approval state, or hide it when the manifest
	 * turns it off. Called on load and again after an approval action changes that state in place.
	 *
	 * @return {Promise<void>} Resolves once the section is drawn; a render error is logged, not thrown.
	 */
	renderSignatureSection()
	{
		const tmpBuiltins = this.pict.settings?.MyDefaultFormManifest?.Builtins;
		if (!HeadlightConfigurableApplication.isBuiltinSectionEnabled(tmpBuiltins?.SignatureSection))
		{
			const tmpSignatureContainer = document.getElementById('Headlight-View-Signatures');
			if (tmpSignatureContainer)
			{
				tmpSignatureContainer.style.display = 'none';
			}
			return Promise.resolve();
		}
		if (!this.pict.views.HeadlightSignatures)
		{
			return Promise.resolve();
		}
		return new Promise((resolve) =>
		{
			this.pict.views.HeadlightSignatures.renderAsync(undefined, undefined, undefined, (error) =>
			{
				if (error)
				{
					this.pict.log.error(`Error rendering HeadlightSignatures view: ${error.message || error}`, { Stack: error.stack });
				}
				resolve();
			});
		});
	}

	/**
	 * Called after authentication has been verified.
	 *
	 * Override this method to perform actions after the user has been authenticated.
	 *
	 * FIXME: need to retool this
	 *
	 * @param {Object<String, any>} data - The data that was loaded.
	 *
	 * @return {Promise<Object<String, any>>}
	 */
	async onDataLoad(data)
	{
		const doc = data?.DocumentData?.ReportData;

		if (!doc)
		{
			this.pict.log.error('Document data not found in onDataLoad', { doc });
		}

		//FIXME: should be handled by document provider implicitly? how does this flow into the login stuff, etc. in a better way
		// The signature render, the input provider and the distilling bundle fetch are independent; run them together.
		const tmpLoadTasks = [ this.renderSignatureSection() ];

		if (this.pict.providers.Input)
		{
			tmpLoadTasks.push(this.pict.providers.Input.onDataLoad(data));
		}
		if (this.pict.providers.Distilling)
		{
			tmpLoadTasks.push(this.pict.providers.Distilling.onDataLoad(data));
		}
		await Promise.all(tmpLoadTasks);
	}

	/**
	 * Resolves the destination for lifecycle comprehension data.
	 *
	 * @return {String} - The destination address for marshalling data.
	 */
	resolveComprehensionMarshallDestination()
	{
		if (this.options.Document?.Required)
		{
			return 'AppData.DocumentData.ReportData.EntityData';
		}
		return 'AppData';
	}

	/**
	 * Resolves the destination for marshalling data.
	 *
	 * @return {String} - The destination address for marshalling data.
	 */
	resolveDataMarshallDestination()
	{
		if (this.options.Document?.Required)
		{
			return 'AppData.DocumentData.ReportData.FormData';
		}
		return 'AppData';
	}

	/**
	 * Lifecycle hook for adding tempplates to the metacontroller.
	 */
	registerTemplates()
	{
		this.pict.addProviderSingleton('PictFormSectionDefaultTemplateProvider', PictSectionForm.PictFormTemplateProvider.default_configuration, PictSectionForm.PictFormTemplateProvider);
		// The `QRCode` input under both the plain pict-section-form prefixes and the HeadLight themes. Apps inject
		// their theme sets after this; those sets don't carry InputType-QRCode, so these templates survive.
		this.qrCodeBundle.injectTemplates(
			{
				Edit: HeadlightQRCodeBundle.DEFAULT_TEMPLATE_PREFIXES.Edit.concat(HeadlightQRCodeBundle.HEADLIGHT_THEME_PREFIXES.Edit),
				Read: HeadlightQRCodeBundle.DEFAULT_TEMPLATE_PREFIXES.Read.concat(HeadlightQRCodeBundle.HEADLIGHT_THEME_PREFIXES.Read),
				Print: HeadlightQRCodeBundle.HEADLIGHT_THEME_PREFIXES.Print,
			});
	}

	/**
	 * Lifecycle method called after the application has been initialized.
	 *
	 * @param {ErrorCallback} fCallback - The callback to call when the method has completed, and indicate if an error occurred.
	 */
	async onInitializeAsync(fCallback)
	{
		super.onInitializeAsync(async (pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}

			this.registerTemplates();

			this.pict.views.PictFormMetacontroller.viewMarshalDestination = this.resolveDataMarshallDestination();
			this.pict.views.PictFormMetacontroller.comprehensionDestinationAddress = this.resolveComprehensionMarshallDestination();
			this.pict.views.PictFormMetacontroller.AutoSolveOnFirstRender = false;

			this.pict.log.info('onInitializeAsync HeadlightConfigurableApplication');

			return fCallback();
		});
	}

	/**
	 * Prepares the application for printing.
	 */
	async prepareForPrintingAsync()
	{
		try
		{
			for (const provider of Object.keys(this.pict.providers))
			{
				if (typeof this.pict.providers[provider]?.completeRender == 'function')
				{
					try
					{
						await this.pict.providers[provider].completeRender();
					}
					catch (error)
					{
						this.pict.log.error(`Error resolving render for provider ${ provider }: ${error.message || error}`, { Stack: error.stack });
					}
				}
			}

			//FIXME: need to add controls for this
			window.status = 'ready_to_print';
		}
		catch (error)
		{
			this.pict.log.error(`Error in onAfterInitializeAsync: ${error.message || error}`, { Stack: error.stack });

			if (this.pict.views.HeadlightPanels)
			{
				// TODO: maybe we want to handle this differently?
				this.pict.views.HeadlightPanels.renderAsync(() =>
				{
					this.pict.log.info('HeadlightPanels rendered via Async with error');
				});
				this.pict.views.HeadlightPaneIssues?.addIssueLog?.('HeadlightPanels rendered via Async with error', error);
			}
		}
	}

	/**
	 * Lifecycle method called after the application has been initialized.
	 *
	 * @param {ErrorCallback} fCallback - The callback to call when the method has completed, and indicate if an error occurred.
	 */
	onAfterInitializeAsync(fCallback)
	{
		super.onAfterInitializeAsync((pError) =>
		{
			if (pError)
			{
				this.pict.log.error(`Error in onAfterInitializeAsync: ${pError.message || pError}`, { Stack: pError.stack });
			}

			// Hook: allow external code to configure the application
			// (e.g. REST client interceptors) before the login/data-load
			// lifecycle begins.  The hook receives the Pict instance so it
			// can wire up services, interceptors, IPC routes, etc.
			if (typeof window._onBeforeFormInit === 'function')
			{
				try
				{
					this.pict.log.info('HeadlightConfigurableApplication: Calling window._onBeforeFormInit hook.');
					window._onBeforeFormInit(this.pict);
				}
				catch (pHookError)
				{
					this.pict.log.error('HeadlightConfigurableApplication: Error in _onBeforeFormInit hook: ' + pHookError, { Stack: pHookError.stack });
				}
			}

			this.loginAsync(async (pError) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error in onAfterInitializeAsync loginAsync: ${pError.message || pError}`, { Stack: pError.stack });
				}

				return fCallback(pError);
			});
		});
	}
	/** @typedef {(pError: (Error | String | null), pSuccess: Boolean) => void} ErrorBooleanCallback */

	/**
	 * Check if the user is authenticated.
	 *
	 * @param {ErrorBooleanCallback} callback - error-first callback to notify on loggedin status (error, true|false)
	 */
	isUserLoggedIn(callback)
	{
		this.pict.HeadlightRestClient.getJSON(`CheckSession`, (pError, pBody) =>
		{
			if (pError || !pBody.LoggedIn)
			{
				// this.AppData.CurrentUser = undefined;
				this.pict.log.trace('session check failed');
				callback(null, false);
				return;
			}
			// Fetch the user record first so that we can use it for generating the QRCode if needed.
			this.pict.HeadlightRestClient.getJSON('User/' + pBody.UserID, (pError, pResult) =>
			{
				if (pResult)
				{
					this.AppData.FullUserRecord = pResult;
				}
				// already logged in
				this.AppData.CurrentUser = pBody;
				callback(null, true);
			});
		});
	}

	/**
	 * Checks if a user is logged in.
	 *
	 * @return {Promise<Boolean>} - A promise that resolves to true if the user is logged in, false otherwise.
	 */
	async checkLogin()
	{
		//FIXME: we should separate login into a provider and a view
		const isLoggedIn = Util.promisify(this.isUserLoggedIn.bind(this));
		return await isLoggedIn();
	}

	/**
	 * @return {boolean}
	 */
	onSolve()
	{
		// only run this if we are in interactive mode
		if (!this.AppData.Interactive)
		{
			return;
		}

		if (!this.loggedIn && this.pict.views.HeadlightLogin && this.options.Login.Required)
		{
			return;
		}

		return super.onSolve();
	}

	/**
	 * Lifecycle method called before the view is rendered.
	 * @return {boolean}
	 */
	onBeforeRender()
	{
		// over-ride some CSS class names for the tab section selector to use Bulma classes
		if (this.pict.providers['Pict-Input-TabSectionSelector'])
		{
			this.pict.providers['Pict-Input-TabSectionSelector'].cssSelectedTabClass = 'is-active';
			this.pict.providers['Pict-Input-TabSectionSelector'].cssHideClass = 'is-hidden';
		}
		// over-ride some CSS class names for the tab group selector to use Bulma classes
		if (this.pict.providers['Pict-Input-TabGroupSelector'])
		{
			this.pict.providers['Pict-Input-TabGroupSelector'].cssSelectedTabClass = 'is-active';
			this.pict.providers['Pict-Input-TabGroupSelector'].cssHideClass = 'is-hidden';
		}

		return super.onBeforeRender();
	}

	/**
	 * Set up the native bridge functions used by the iOS configurable form
	 * WKWebView. This mode is activated when the page is captured via
	 * single-file with ?Mode=OfflineTemplate. The captured HTML is later
	 * loaded from the device filesystem; the native layer injects a
	 * StorageAdapter and DocumentBridge via WKUserScript, then calls
	 * initFormWithManifest() to bootstrap the form.
	 *
	 * This is a static method so all subclasses (HeadlightDynamicForm,
	 * HeadlightDocument, etc.) inherit it. Each HTML template calls:
	 *   AppClass.setupOfflineTemplate(AppClass);
	 *
	 * @param {typeof HeadlightConfigurableApplication} pAppClass - The application class to use for form initialization.
	 */
	static setupOfflineTemplate(pAppClass)
	{
		/**
		 * Initialise the PICT configurable-form application with the
		 * given manifest and optional bundle (project reference data).
		 *
		 * Called by the iOS native layer after the page finishes loading.
		 *
		 * @param {object} pManifest - The resolved form manifest JSON.
		 * @param {object} [pBundle] - Optional project bundle/reference data.
		 */
		window.initFormWithManifest = (pManifest, pBundle) =>
		{
			window._InteractiveForm = true;
			window._OfflineMode = true;
			window._OfflineBundleData = pBundle || {};

			pAppClass.default_configuration.pict_configuration =
				pAppClass.preprocessFormDefinition(pManifest);

			Pict.safeLoadPictApplication(pAppClass, 3);
		};

		/**
		 * Load a document record from native storage and marshal it into
		 * the live form.  Called by the iOS native layer after
		 * initFormWithManifest has completed.
		 *
		 * @param {string} pGUID - The GUIDDocument or external index to load.
		 */
		window.nativeLoadDocument = (pGUID) =>
		{
			if (!window._configurableFormDocumentBridge)
			{
				console.error('[OfflineTemplate] _configurableFormDocumentBridge not available');
				return;
			}

			window._configurableFormDocumentBridge.loadDocument(pGUID, (pDocData) =>
			{
				if (!window._Pict || !window._Pict.PictApplication)
				{
					console.error('[OfflineTemplate] PICT application not ready');
					return;
				}

				const tmpApp = window._Pict.PictApplication;
				tmpApp.AppData.DocumentData = pDocData;

				// Bootstrap form views from manifest if not yet done
				if (typeof tmpApp.bootstrapPictFormViewsFromManifest === 'function')
				{
					tmpApp.bootstrapPictFormViewsFromManifest();
				}

				tmpApp.render();

				// Marshal document data into form fields
				if (tmpApp.pict && tmpApp.pict.views && tmpApp.pict.views.PictFormMetacontroller)
				{
					tmpApp.pict.views.PictFormMetacontroller.marshalToView();
				}

				// Offline, the document arrives here rather than through the load lifecycle, so this is
				// the first moment auto-save can tell whether the record is editable. Saves route back
				// through nativeSaveCurrentForm to the native bridge, not the REST client.
				tmpApp.startAutoSave();
			});
		};

		/**
		 * Run a full application save. Called by the iOS native layer before dismissing the form view
		 * controller, and by auto-save on its interval.
		 *
		 * This is the SAME save the online form runs: the whole lifecycle, so every provider's
		 * `onBeforeSaveDataAsync` marshals and emits, and only the transport underneath differs
		 * (`HeadlightDocumentService` writes through the native bridge instead of PUTting). Persisting
		 * the record directly from here would skip the marshal the form applications do and would never
		 * build `Document.EntityData`, so nothing the form created would exist once the device synced.
		 *
		 * Resolves once the save has been persisted, so the caller can await it before tearing the
		 * webview down. `callAsyncJavaScript:` resolves the returned promise; a caller using
		 * `evaluateJavaScript:` gets no such guarantee and will race the teardown.
		 *
		 * @return {Promise<{Saved: boolean, Error?: string}>} The outcome of the save.
		 */
		window.nativeSaveCurrentForm = async () =>
		{
			if (!window._Pict || !window._Pict.PictApplication)
			{
				return { Saved: false, Error: 'The form application is not ready.' };
			}

			const tmpApp = window._Pict.PictApplication;
			const tmpDocumentProvider = tmpApp.pict && tmpApp.pict.providers && tmpApp.pict.providers.DocumentProvider;
			if (!tmpDocumentProvider || typeof tmpDocumentProvider.handleSaveAsync !== 'function')
			{
				const tmpMessage = 'No DocumentProvider is registered, so this form has no save lifecycle to run.';
				tmpApp.pict.log.error(`nativeSaveCurrentForm: ${tmpMessage}`);
				return { Saved: false, Error: tmpMessage };
			}

			const tmpError = await tmpDocumentProvider.handleSaveAsync(true);
			if (tmpError)
			{
				return { Saved: false, Error: tmpError.message || String(tmpError) };
			}
			return { Saved: true };
		};

		/**
		 * Wire the native storage adapter (injected by iOS via
		 * ConfigurableFormStorageAdapter.js) into the FormPersistence
		 * provider once the PICT application has initialised.
		 *
		 * Can be called from native or from initFormWithManifest's
		 * post-init hook.
		 */
		window.setupNativeFormPersistence = () =>
		{
			if (!window._Pict || !window._nativeStorageAdapter)
			{
				return;
			}
			const tmpPersistence = window._Pict.providers && window._Pict.providers.FormPersistence;
			if (tmpPersistence && typeof tmpPersistence.setStorageAdapter === 'function')
			{
				tmpPersistence.setStorageAdapter(window._nativeStorageAdapter);
			}
		};
	}
}

/** @type {Record<string, any>} */
HeadlightConfigurableApplication.default_configuration = Object.assign(
{
	'MainViewportViewIdentifier': 'PictFormMetacontroller',
},
HeadlightConfigurableApplication.default_configuration,
{
	'AutoRenderMainViewportViewAfterInitialize': false,
});
/** @type {Record<string, any>} */
HeadlightConfigurableApplication.pict_configuration = require('./application-pict-config.json');

module.exports = HeadlightConfigurableApplication;
